<template>
  <div class="base-multiselect">
    <div class="base-multiselect__wrapper">
      <div class="base-multiselect__container">
        <div
          :class="[
            'base-multiselect__value',
            { 'open-options': showItems },
            { 'has-value': multiselectValue?.length },
            { 'can-clear': canClear && multiselectValue?.length },
            { disabled: disabled },
          ]"
          tabindex="0"
          @click.stop="openOption"
        >
          <div class="value__wrapper cursor-pointer">
            <div class="value__container">
              <ul class="value__list" ref="valueList">
                <li
                  class="value__item"
                  v-for="value in valuesSelected"
                  :key="value?.id || value"
                >
                  <slot name="value" :value="value">
                    <span :class="{ value__tag: chips }">
                      {{ valueProp ? value[valueProp] : value
                      }}<span class="item__separation" v-if="!chips">,</span>
                    </span>
                  </slot>
                  <button
                    v-if="chips"
                    type="button"
                    class="value__remove-btn"
                    @click.stop="removeItem(value)"
                  >
                    <span class="remove-btn__icon">+</span>
                  </button>
                </li>
                <li
                  class="value__item"
                  v-if="multiselectValue.length > visibleLimit"
                >
                  {{ "... +" + (multiselectValue.length - visibleLimit) }}
                </li>
              </ul>
            </div>
            <span v-if="placeholder && !disabled" class="value__placeholder">{{
              placeholder
            }}</span>
            <button
              v-if="canClear && multiselectValue?.length && !disabled"
              type="button"
              class="value__clear-btn"
              @click.stop="clearValue"
            >
              <span class="clear-btn__icon">+</span>
            </button>

            <span class="value__arrow cursor-pointer">
              <span class="arrow__icon"></span>
            </span>
          </div>
        </div>
        <div class="base-multiselect__select" v-if="showItems">
          <div class="select__wrapper">
            <div class="selection__container">
              <div class="selection__options">
                <input
                  v-if="search"
                  placeholder="Type to search"
                  name="searchQuery"
                  type="text"
                  autocomplete="off"
                  spellcheck="false"
                  tabindex="0"
                  class="search-input"
                  ref="input"
                  :value="searchQuery"
                  @click.stop
                  @input="updateSearchQuery($event)"
                />
                <ul
                  class="options__list"
                  v-if="options.length"
                  @scroll="handleScroll"
                >
                  <li
                    class="options__item"
                    :class="{ 'cursor-pointer': !disabled }"
                    tabindex="-1"
                    v-for="option in filtredOptions"
                    :key="option?.id || option"
                    @click.stop
                  >
                    <input
                      :disabled="disabled"
                      :checked="areOptionChecked(option)"
                      class="item__input"
                      :type="selectionType"
                      name="multiselectValue"
                      :id="option?.id || option"
                      :value="option"
                      v-model="multiselectValue"
                      @change="handleChange"
                      hidden
                    />
                    <label class="item__label" :for="option?.id || option">
                      <slot name="label" :label="option"
                        >{{ labelProp ? option[labelProp] : option }}
                      </slot>
                    </label>
                  </li>
                </ul>
                <h3 v-else>No item in options list</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="base-multiselect__description" v-if="description">
        <p
          class="description__text"
          v-html="description?.text || description"
        ></p>
      </div>
    </div>
  </div>
</template>

<script>
import { isObject, hasProperty } from "@/helpers";
import EntityApi from "@/api/entityApi";
export default {
  name: "BaseMultiselect",
  model: {
    prop: "modelValue",
    event: "update:model-value",
  },
  props: {
    placeholder: {
      type: [String, Object],
      default: null,
    },
    items: {
      type: [String, Object, Array],
      default: null,
    },
    value: {
      type: [String, Number, Array, Object],
      // default: null,
    },
    modelValue: {
      type: [String, Number, Array, Object],
      // default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    labelProp: {
      type: String,
    },
    valueProp: {
      type: String,
    },
    canClear: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    description: {
      type: [String, Object],
      default: null,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:modelValue",
    "select",
    "removeItem",
    "openOption",
    "closeOption",
  ],
  data() {
    return {
      showItems: false,
      options: null,
      showLoader: false,
      hasMore: true,
      searchQuery: "",
      debounce: null,
      debounceTime: 1000,

      visibleLimit: 1000,
      resizeObserver: null,
    };
  },
  watch: {
    multiselectValue: {
      handler() {
        this.$nextTick(() => {
          this.calculateVisibleItems();
        });
      },
      deep: true,
    },
  },
  computed: {
    multiselectValue: {
      get() {
        if (this.modelValue || this.modelValue === null) {
          return this.correctModelValue;
        } else {
          return this.correctValue;
        }
      },
      set(value) {
        this.updateMultiselectValue(value);
      },
    },
    valuesSelected() {
      if (!this.multiselectValue) return [];
      if (this.multiselectValue.length > this.visibleLimit) {
        return this.multiselectValue.slice(0, this.visibleLimit);
      }
      return this.multiselectValue;
    },
    correctValue() {
      if (this.value) {
        if (Array.isArray(this.value)) {
          return this.value;
        } else {
          return [this.value];
        }
      } else return [];
    },
    correctModelValue() {
      if (this.modelValue) {
        if (Array.isArray(this.modelValue)) {
          return this.modelValue;
        } else {
          return [this.modelValue];
        }
      } else return [];
    },
    selectionType() {
      if (this.multiple) {
        return "checkbox";
      } else {
        return "radio";
      }
    },
    filtredOptions() {
      if (!this.search) {
        return this.options;
      }
      if (this.search && this.searchQuery?.length > 0) {
        return this.options.filter((el) => {
          const normalizedLabelProp = el?.[this.labelProp]?.toLowerCase() || "";
          const normalizedSearchQuery = this.searchQuery.toLowerCase();
          return normalizedLabelProp.includes(normalizedSearchQuery);
        });
      } else return this.options;
    },
  },
  methods: {
    calculateVisibleItems() {
      if (!this.$refs.valueList) return;

      const containerWidth = this.$refs.valueList.clientWidth;
      if (containerWidth === 0) return; // Not visible yet

      // Reserve space for the "+ N" counter and right padding
      // 50px for counter, + extra if clear/arrow take space inside value__list
      const bufferSpace = 60;
      const availableWidth = containerWidth - bufferSpace;

      // Create a reusable canvas context for measuring text
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      // Get font styles from the list element to ensure accuracy
      const style = window.getComputedStyle(this.$refs.valueList);
      context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

      let currentWidth = 0;
      let count = 0;

      // Chip styling constants (approximate based on CSS)
      // Chips: padding-left(10) + padding-right(10) + margin-left(8) + close-btn(16 approx)
      const chipOverhead = this.chips ? 44 : 10;

      for (const item of this.multiselectValue) {
        // Determine text to measure
        const text = this.valueProp ? item[this.valueProp] : String(item);

        // Measure text width
        const textMetrics = context.measureText(text);
        const itemWidth = textMetrics.width + chipOverhead;

        if (currentWidth + itemWidth < availableWidth) {
          currentWidth += itemWidth;
          count++;
        } else {
          break; // Stop loop if we exceed width
        }
      }

      // If we can show all, assume length, otherwise use calculated count
      // Ensure we show at least 1 item if there is a value
      if (this.multiselectValue.length > 0 && count === 0) count = 1;

      this.visibleLimit = count;
    },
    updateSearchQuery(e) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.searchQuery = e.target.value;
      }, this.debounceTime);
    },
    initOptions(data) {
      if (
        data &&
        !["valueName", "titleName"].some((key) =>
          Object.keys(data).includes(key)
        )
      ) {
        if (Array.isArray(data)) {
          return data;
        } else {
          return [data];
        }
      } else return [];
    },
    async openOption() {
      // if (this.disabled) return;
      if (this.showItems) return this.closeOption();
      if (isObject(this.items) && hasProperty(this.items, "entity")) {
        await this.loadMore();
        this.showItems = true;
      } else {
        this.showItems = true;
      }
      document.addEventListener("click", this.hideOption);
      this.$emit("openOption");
    },
    hideOption(e) {
      console.log("hide Option");
      if (e.target.dataset.action === "open") {
        return;
      }
      this.closeOption();
    },
    closeOption() {
      console.log("close option");
      this.showItems = false;
      document.removeEventListener("click", this.hideOption);
      this.$emit("closeOption");
    },
    async handleScroll(event) {
      const el = event.target;
      if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 35) {
        await this.loadMore();
      }
    },
    async loadMore() {
      if (
        this.showLoader ||
        (!this.hasMore && this.options.length) ||
        this.searchQuery
      )
        return;
      this.showLoader = true;

      const fieldOptions = await EntityApi.getEntityItemsLimit(
        this.items.entity,
        this.options.length,
        300
      );

      if (Array.isArray(fieldOptions) && fieldOptions.length < 300) {
        this.hasMore = false;
      }
      this.options = [...this.options, ...fieldOptions];

      this.showLoader = false;
    },
    areOptionChecked(option) {
      if (!this.multiselectValue || !option) return false;
      let checked = false;

      if (
        typeof option !== "object" &&
        this.multiselectValue?.includes(option)
      ) {
        checked = true;
      }

      if (typeof option === "object") {
        this.multiselectValue.some((val) => {
          if (
            (val && option[this.valueProp] === val[this.valueProp]) ||
            option.id === val.id
          ) {
            checked = true;
          }
        });
      }

      return checked;
    },
    handleChange() {
      if (this.disabled) return;
      console.log("handleChange");
      if (this.selectionType === "radio" || this.closeOnSelect)
        this.closeOption();
      if (this.modelValue || this.modelValue === null) return;
      //if(this.multiple)this.$emit("select", this.multiselectValue);
    },
    clearValue() {
      this.updateMultiselectValue(null);
    },
    removeItem(value) {
      const selectedValue = this.multiselectValue;
      const filteredValue = selectedValue.filter((item) => item !== value);
      if (filteredValue.length) {
        this.updateMultiselectValue(filteredValue);
      } else {
        this.updateMultiselectValue(null);
      }
      this.$emit("removeItem", value);
    },
    updateMultiselectValue(value) {
      if (this.modelValue || this.modelValue === null) {
        this.$emit("update:modelValue", value);
      } else {
        this.$emit("select", value);
      }
    },
  },
  created() {
    this.options = this.initOptions(this.items);
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateVisibleItems();
    });
    if (this.$refs.valueList) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleItems();
      });
      this.resizeObserver.observe(this.$refs.valueList);
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener("click", this.hideOption);
  },
};
</script>

<style lang="scss" scoped>
ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.base-multiselect {
  font-size: var(--ms-font-size, 1rem);
  width: 100%;
  /* &:focus-within .base-multiselect__select {
    display: block;
  } */
}
.base-multiselect__wrapper {
  .base-multiselect__description {
    padding: 0.25rem;
    font-size: 0.8rem;
    line-height: 1;
  }
}
.base-multiselect__container {
  position: relative;

  .base-multiselect__value {
    position: relative;
    min-height: calc(
      2 * var(--bms-border-width, 1px) + var(--bms-font-size, 1rem) *
        var(--bms-line-height, 1.375) + 2 * var(--ms-py, 0.5rem)
    );
    width: 100%;
    border: var(--bms-border-width, 1px) solid var(--bms-border-color, #000);
    border-radius: var(--bms-border-radius, 4px);
    background-color: var(--bms-bg, #ffffff);

    &:hover {
      border-color: var(--bms-border-color-hover, #acacff);
    }

    &.open-options {
      border-color: var(--bms-border-color-hover, #acacff);
      .value__arrow {
        transform: rotate(180deg) translate(-50%, 50%);
      }
    }
    &.has-value {
      .value__placeholder {
        top: 0;
        left: 0.5rem;
        transform: translateY(-50%);
        font-size: 0.7rem;
        line-height: 1;
        background-color: var(--bms-bg, #ffffff);
        border-radius: 4px;
        padding: 2px;
        color: inherit;
      }
    }
    &.can-clear {
      .value__container {
        padding-right: 3rem;
      }
    }
    .value__wrapper {
    }
    .value__container {
      padding: 0.4rem;
      padding-right: 2rem;
      .value__list {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }
      .value__item {
        position: relative;

        &:last-child .item__separation {
          display: none;
        }
        .value__tag {
          padding: 4px 10px;
          overflow: hidden;
          background-color: var(--bms-tag-bg, #e8e9eb);
          border-radius: 9999px;
        }
        &:hover .value__remove-btn {
          display: flex;
        }
        .value__remove-btn {
          position: absolute;
          top: 0;
          left: 100%;
          transform: translate(-75%, -25%);
          width: 1rem;
          height: 1rem;
          color: #ffffff;
          background-color: #000000;
          border-radius: 50%;
          margin: 0;
          padding: 0;
          /* display: flex; */
          align-items: center;
          justify-content: center;
          border: 2px solid grey;
          display: none;
          z-index: 1;

          &:hover {
            color: var(--bms-border-color-hover, #acacff);
          }
        }
        .remove-btn__icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-40%, -55%) rotate(45deg);
        }
      }
      .value__item + .value__item {
        margin-left: 8px;
      }
    }
    .value__placeholder {
      position: absolute;
      top: 50%;
      left: 0.5rem;
      transform: translateY(-50%);
      color: var(--bms-placeholder-color, #c1c1c1);
      transition: transform 250ms ease-out, top 250ms ease-out,
        font-size 250ms ease-out;
    }
    /* &:focus-within .value__placeholder {
      top: 0;
      left: 0.5rem;
      transform: translateY(-50%);
      font-size: 0.7rem;
      line-height: 1;
      background-color: var(--bms-bg, #ffffff);
      border-radius: 4px;
      padding: 2px;
      color: var(--bms-border-color-hover, #acacff);
    } */
    .value__clear-btn {
      position: absolute;
      top: 50%;
      right: 2rem;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
      font-size: 1.5rem;
      color: var(--bms-placeholder-color, #c1c1c1);
      border: none;
      background: none;
      cursor: pointer;

      &:hover,
      &:focus {
        color: inherit;
      }
    }
    .clear-btn__icon {
      transform: rotate(45deg);
    }
    .value__arrow {
      position: absolute;
      top: 50%;
      right: 1rem;
      transform: translate(50%, -50%);
      transition: transform 250ms ease-out;

      width: 1rem;
      height: 1rem;

      & .arrow__icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-width: 6px 6px 0 6px;
        border-color: var(--bms-border-color, #000000) transparent transparent
          transparent;
        border-style: solid;
      }
    }
  }
  .base-multiselect__select {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    /* display: none; */
    z-index: 100;
    background-color: var(--bms-select-bg, #ffffff);
    border: var(--bms-select-border-width, 1px) solid
      var(--bms-select-border-color, #dddde3);
    .selection__container {
    }
    .select__option {
    }
    .options__list {
      max-height: 25vh;
      overflow-y: auto;
      scroll-behavior: smooth;
    }

    /* -------- style for scrollbar -------- */
    .options__list::-webkit-scrollbar {
      width: 0.3rem;
      height: 0.3rem;
    }

    .options__list:hover::-webkit-scrollbar {
      width: 0.3rem;
      height: 0.3rem;
    }

    .options__list::-webkit-scrollbar-track {
      background-color: transparent;
    }

    .options__list::-webkit-scrollbar-thumb {
      border-radius: 100px;
      background: grey;
    }
    /* -------- ----- --- --------- -------- */
    .options__item {
      &:hover {
        background-color: var(--bms-option-hover, rgba(33, 150, 243, 0.1));
      }
      &:focus {
        /* background-color: var(--bms-option-focus, rgba(33, 150, 243, 0.3)); */
      }

      .item__input {
      }
      .item__input:checked ~ .item__label {
        background-color: var(--bms-option-focus, rgba(33, 150, 243, 0.3));
      }
      .item__label {
        padding: 0.5rem;
        width: 100%;
      }
    }
  }
  .disabled {
    background-color: #e2e8f0;
    border-color: #e2e8f0;
    cursor: default;

    &:hover {
      border-color: #e2e8f0;
    }
    .value__arrow .arrow__icon {
      border-block-color: initial;
    }
  }
}

.options__item {
  width: 100%;
}

.options__item .item__label {
  width: 100%;
  display: block;
}
.search-input {
  width: 100%;
  border: 1px solid #dfdcdc;
  border-radius: 7px;
  margin-top: 5px;
  margin-bottom: 10px;
  padding: 5px 5px;
}
</style>
